import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Clock, CheckCircle, AlertCircle } from "lucide-react";
import api from "../utils/api";

function fmtIST(iso) {
  return new Date(iso).toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
    timeZone: "Asia/Kolkata",
  });
}

export default function ActivityModal({ onClose }) {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/api/v/call-logs");
      setLogs(data);
    } catch (err) {
      setError(err.message || "Failed to load activity log.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{
          position: "fixed", inset: 0, zIndex: 900,
          background: "rgba(17,24,39,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ scale: 0.96, y: 16, opacity: 0 }}
          animate={{ scale: 1,    y: 0,  opacity: 1 }}
          exit={{    scale: 0.96, y: 16, opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          style={{
            width: "100%", maxWidth: 720,
            background: "#ffffff", border: "1px solid #E5E7EB",
            borderRadius: 16, overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.14)",
            maxHeight: "90vh", display: "flex", flexDirection: "column",
          }}
        >
          {/* Header */}
          <div style={{
            padding: "18px 22px", borderBottom: "1px solid #F3F4F6",
            display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Clock size={16} color="#2563EB" />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>API Activity Log</div>
                <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 1 }}>Your recent vehicle lookups (last 200)</div>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center" }}
            ><X size={14} /></button>
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflowY: "auto" }}>
            {loading && (
              <div style={{ padding: 48, textAlign: "center", color: "#9CA3AF", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                <Loader2 size={20} style={{ animation: "spin 1s linear infinite" }} />
                <span style={{ fontSize: 13 }}>Loading your activity…</span>
              </div>
            )}

            {!loading && error && (
              <div style={{ margin: 20, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "#DC2626", display: "flex", alignItems: "center", gap: 8 }}>
                <AlertCircle size={14} /> {error}
              </div>
            )}

            {!loading && !error && logs.length === 0 && (
              <div style={{ padding: 48, textAlign: "center", color: "#9CA3AF" }}>
                <Clock size={28} style={{ marginBottom: 10, opacity: 0.4 }} />
                <div style={{ fontSize: 14 }}>No API calls yet.</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>Add a vehicle and track it to see activity here.</div>
              </div>
            )}

            {!loading && !error && logs.length > 0 && (
              <>
                {/* Column headers */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1.2fr 0.8fr 0.8fr 0.7fr",
                  padding: "8px 20px",
                  background: "#F9FAFB", borderBottom: "1px solid #F3F4F6",
                }}>
                  {["Time (IST)", "Vehicle No.", "Endpoint", "Status", "Duration"].map(h => (
                    <span key={h} style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4 }}>{h}</span>
                  ))}
                </div>

                {/* Rows */}
                {logs.map((log, i) => (
                  <div
                    key={i}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1.2fr 0.8fr 0.8fr 0.7fr",
                      padding: "10px 20px",
                      borderBottom: i < logs.length - 1 ? "1px solid #F9FAFB" : "none",
                      alignItems: "center",
                      transition: "background 0.1s",
                    }}
                    onMouseOver={e => e.currentTarget.style.background = "#FAFAFA"}
                    onMouseOut={e  => e.currentTarget.style.background = "transparent"}
                  >
                    <div style={{ fontSize: 12, color: "#374151" }}>{fmtIST(log.calledAt)}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#111827", fontFamily: "monospace" }}>
                      {log.vehicleNo || <span style={{ color: "#9CA3AF", fontFamily: "inherit", fontWeight: 400 }}>—</span>}
                    </div>
                    <div style={{ fontSize: 11, color: "#6B7280" }}>{log.endpoint}</div>
                    <div>
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99,
                        background: log.status === "success" ? "#F0FDF4" : "#FEF2F2",
                        color:      log.status === "success" ? "#16A34A" : "#DC2626",
                        border:     `1px solid ${log.status === "success" ? "#BBF7D0" : "#FECACA"}`,
                        display: "inline-flex", alignItems: "center", gap: 4,
                      }}>
                        {log.status === "success"
                          ? <CheckCircle size={9} />
                          : <AlertCircle size={9} />
                        }
                        {log.status}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "#6B7280" }}>
                      {log.durationMs != null ? `${log.durationMs}ms` : "—"}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Footer */}
          {!loading && logs.length > 0 && (
            <div style={{ padding: "12px 20px", borderTop: "1px solid #F3F4F6", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <span style={{ fontSize: 12, color: "#9CA3AF" }}>{logs.length} record{logs.length !== 1 ? "s" : ""}</span>
              <button
                onClick={load}
                style={{ fontSize: 12, fontWeight: 600, color: "#2563EB", background: "none", border: "1px solid #BFDBFE", borderRadius: 6, padding: "5px 14px", cursor: "pointer" }}
              >Refresh</button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
